/**
 * Reads the immutable presentation snapshot baked into PROD uploader
 * builds. The uploader's CI injects `window.__CONFIG__` into index.html
 * at release time (see prod-deploy.yml). When present, the app renders
 * instantly from this snapshot with zero runtime presentation fetch.
 */
import type { FontScheme } from "./fontScheme";
import type { TimelineConfig } from "./timeline";
import type { MenuConfig } from "./menu";

export interface BakedConfig {
  siteId: string;
  name: string;
  displayName: string | null;
  slug: string;
  /** ISO-8601 string captured at release time (display only). */
  expiresAt: string | null;
  version: string;
  releasedAt: string;
  config: {
    language: string;
    colorScheme?: { palette: Record<string, string> };
    fontScheme?: FontScheme;
    /** Optional on older snapshots; resolved with defaults by useSite. */
    permissions?: {
      allowGallery?: boolean;
      allowSingleImageDownload?: boolean;
      allowZipDownload?: boolean;
      allowTimeline?: boolean;
      allowMenu?: boolean;
      allowDjRequests?: boolean;
    };
    /** Per-site wedding-day timeline (present only when configured). */
    timeline?: TimelineConfig;
    /** Per-site food & drink menu (present only when configured). */
    menu?: MenuConfig;
  };
  texts: {
    language: string;
    keys: Record<string, string>;
  };
}

declare global {
  interface Window {
    __CONFIG__?: BakedConfig;
  }
}

/**
 * Returns the baked snapshot if this is a PROD build, else `null`
 * (runtime/TEST build that fetches config via `getSiteConfig`).
 */
export function readBakedConfig(): BakedConfig | null {
  const baked = window.__CONFIG__;
  if (!baked || typeof baked !== "object") return null;
  if (typeof baked.slug !== "string" || !baked.config) return null;
  return baked;
}
